/**
 * Knowledge Repository
 * 
 * Consolidates knowledge base systems:
 * - knowledge_articles
 * - knowledge_base_articles  
 * - agent_knowledge_base
 * - general_knowledge_base
 * 
 * Into unified knowledge_unified table
 * 
 * @example
 * ```typescript
 * const repo = new KnowledgeRepository();
 * 
 * // Find all articles
 * const articles = await repo.findAll();
 * 
 * // Search articles
 * const results = await repo.search('HVAC troubleshooting');
 * 
 * // Find by category
 * const hvacArticles = await repo.findByCategory('hvac');
 * 
 * // Get statistics
 * const stats = await repo.getStatistics();
 * ```
 */

import { BaseRepository, QueryOptions } from './BaseRepository';

/**
 * Knowledge Article Type
 */
export interface KnowledgeArticle {
  id: string;
  company_id: string;
  
  // Core fields
  title: string;
  content: string;
  summary?: string;
  category: string;
  subcategory?: string;
  tags?: string[];
  
  // Article metadata
  article_type: 'general' | 'technical' | 'troubleshooting' | 'training' | 'faq' | 'policy';
  target_audience: 'agent' | 'customer' | 'technician' | 'all';
  skill_level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  
  // Status and visibility
  status: 'draft' | 'review' | 'published' | 'archived';
  is_public: boolean;
  is_featured: boolean;
  
  // Version control
  version: number;
  previous_version_id?: string;
  
  // Search optimization
  keywords?: string[];
  search_vector?: string;
  
  // Usage tracking
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  last_viewed_at?: string;
  
  // Media and attachments
  featured_image?: string;
  attachments?: Array<{
    id: string;
    name: string;
    url: string;
    type: string;
    size: number;
  }>;
  
  // Related content
  related_article_ids?: string[];
  prerequisite_article_ids?: string[];
  
  // Author and review
  created_by: string;
  updated_by?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  
  // Source tracking (for migration)
  source_system?: 'knowledge_articles' | 'knowledge_base_articles' | 'agent_knowledge_base' | 'general_knowledge_base';
  source_id?: string;
  
  // Timestamps
  created_at: string;
  updated_at: string;
  published_at?: string;
  archived_at?: string;
}

/**
 * Knowledge Article Filters
 */
export interface KnowledgeFilters {
  company_id?: string;
  category?: string;
  subcategory?: string;
  article_type?: KnowledgeArticle['article_type'] | KnowledgeArticle['article_type'][];
  target_audience?: KnowledgeArticle['target_audience'] | KnowledgeArticle['target_audience'][];
  skill_level?: KnowledgeArticle['skill_level'] | KnowledgeArticle['skill_level'][];
  status?: KnowledgeArticle['status'] | KnowledgeArticle['status'][];
  is_public?: boolean;
  is_featured?: boolean;
  created_by?: string;
  tags?: string[];
  search_query?: string;
  min_helpful_count?: number;
  created_after?: string;
  created_before?: string;
  updated_after?: string;
  updated_before?: string;
}

/**
 * Knowledge Statistics
 */
export interface KnowledgeStatistics {
  total_articles: number;
  published_articles: number;
  draft_articles: number;
  archived_articles: number;
  total_views: number;
  total_helpful: number;
  total_not_helpful: number;
  average_helpfulness: number;
  by_category: Record<string, number>;
  by_type: Record<string, number>;
  by_audience: Record<string, number>;
  most_viewed: Array<{
    id: string;
    title: string;
    view_count: number;
  }>;
  most_helpful: Array<{
    id: string;
    title: string;
    helpful_count: number;
  }>;
  recently_updated: Array<{
    id: string;
    title: string;
    updated_at: string;
  }>;
}

/**
 * Search Result with relevance score
 */
export interface KnowledgeSearchResult extends KnowledgeArticle {
  relevance_score?: number;
  matched_fields?: string[];
}

/**
 * Knowledge Repository Class
 * 
 * Provides data access methods for knowledge articles with
 * advanced search, filtering, and analytics capabilities.
 */
export class KnowledgeRepository extends BaseRepository<KnowledgeArticle> {
  constructor() {
    super({
      tableName: 'knowledge_unified',
      primaryKey: 'id'
    });
  }

  /**
   * Find articles by filters
   */
  async findByFilters(
    filters: KnowledgeFilters,
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    let query = this.client.from(this.config.tableName).select('*');

    // Apply filters
    if (filters.company_id) {
      query = query.eq('company_id', filters.company_id);
    }

    if (filters.category) {
      query = query.eq('category', filters.category);
    }

    if (filters.subcategory) {
      query = query.eq('subcategory', filters.subcategory);
    }

    if (filters.article_type) {
      if (Array.isArray(filters.article_type)) {
        query = query.in('article_type', filters.article_type);
      } else {
        query = query.eq('article_type', filters.article_type);
      }
    }

    if (filters.target_audience) {
      if (Array.isArray(filters.target_audience)) {
        query = query.in('target_audience', filters.target_audience);
      } else {
        query = query.eq('target_audience', filters.target_audience);
      }
    }

    if (filters.skill_level) {
      if (Array.isArray(filters.skill_level)) {
        query = query.in('skill_level', filters.skill_level);
      } else {
        query = query.eq('skill_level', filters.skill_level);
      }
    }

    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }

    if (filters.is_public !== undefined) {
      query = query.eq('is_public', filters.is_public);
    }

    if (filters.is_featured !== undefined) {
      query = query.eq('is_featured', filters.is_featured);
    }

    if (filters.created_by) {
      query = query.eq('created_by', filters.created_by);
    }

    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    if (filters.min_helpful_count !== undefined) {
      query = query.gte('helpful_count', filters.min_helpful_count);
    }

    if (filters.created_after) {
      query = query.gte('created_at', filters.created_after);
    }

    if (filters.created_before) {
      query = query.lte('created_at', filters.created_before);
    }

    if (filters.updated_after) {
      query = query.gte('updated_at', filters.updated_after);
    }

    if (filters.updated_before) {
      query = query.lte('updated_at', filters.updated_before);
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    } else {
      // Default ordering by updated_at
      query = query.order('updated_at', { ascending: false });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  }

  /**
   * Search articles using full text search
   */
  async search(
    searchQuery: string,
    filters?: Omit<KnowledgeFilters, 'search_query'>,
    options?: QueryOptions
  ): Promise<KnowledgeSearchResult[]> {
    // Use PostgreSQL full text search if available
    let query = this.client
      .from(this.config.tableName)
      .select('*')
      .textSearch('search_vector', searchQuery, {
        type: 'websearch',
        config: 'english'
      });

    // Apply additional filters if provided
    if (filters) {
      if (filters.company_id) query = query.eq('company_id', filters.company_id);
      if (filters.category) query = query.eq('category', filters.category);
      if (filters.status) {
        if (Array.isArray(filters.status)) {
          query = query.in('status', filters.status);
        } else {
          query = query.eq('status', filters.status);
        }
      }
      if (filters.target_audience) {
        if (Array.isArray(filters.target_audience)) {
          query = query.in('target_audience', filters.target_audience);
        } else {
          query = query.eq('target_audience', filters.target_audience);
        }
      }
    }

    // Apply ordering
    if (options?.orderBy) {
      query = query.order(options.orderBy.column, { 
        ascending: options.orderBy.ascending 
      });
    }

    // Apply pagination
    if (options?.limit) {
      const from = options.offset || 0;
      const to = from + options.limit - 1;
      query = query.range(from, to);
    }

    const { data, error } = await query;

    if (error) throw error;
    return (data || []) as KnowledgeSearchResult[];
  }

  /**
   * Find articles by category
   */
  async findByCategory(
    category: string,
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    return this.findByFilters({ category }, options);
  }

  /**
   * Find articles by tags
   */
  async findByTags(
    tags: string[],
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    return this.findByFilters({ tags }, options);
  }

  /**
   * Find featured articles
   */
  async findFeatured(
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    return this.findByFilters({ is_featured: true, status: 'published' }, options);
  }

  /**
   * Find published articles
   */
  async findPublished(
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    return this.findByFilters({ status: 'published' }, options);
  }

  /**
   * Find articles by target audience
   */
  async findByAudience(
    audience: KnowledgeArticle['target_audience'],
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    return this.findByFilters({ target_audience: audience, status: 'published' }, options);
  }

  /**
   * Find related articles
   */
  async findRelated(
    articleId: string,
    limit: number = 5
  ): Promise<KnowledgeArticle[]> {
    const article = await this.findById(articleId);
    if (!article) return [];

    // Find articles with similar tags or same category
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .eq('status', 'published')
      .neq('id', articleId)
      .or(`category.eq.${article.category},tags.cs.{${article.tags?.join(',') || ''}}`)
      .limit(limit);

    if (error) throw error;
    return data || [];
  }

  /**
   * Get article versions
   */
  async getVersions(
    articleId: string
  ): Promise<KnowledgeArticle[]> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('*')
      .or(`id.eq.${articleId},previous_version_id.eq.${articleId}`)
      .order('version', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Increment view count
   */
  async incrementViews(articleId: string): Promise<void> {
    const { error } = await this.client.rpc('increment_article_views', {
      article_id: articleId
    });

    if (error) {
      // Fallback if RPC doesn't exist
      const article = await this.findById(articleId);
      if (article) {
        await this.update(articleId, {
          view_count: article.view_count + 1,
          last_viewed_at: new Date().toISOString()
        });
      }
    }
  }

  /**
   * Mark article as helpful
   */
  async markHelpful(articleId: string): Promise<void> {
    const article = await this.findById(articleId);
    if (article) {
      await this.update(articleId, {
        helpful_count: article.helpful_count + 1
      });
    }
  }

  /**
   * Mark article as not helpful
   */
  async markNotHelpful(articleId: string): Promise<void> {
    const article = await this.findById(articleId);
    if (article) {
      await this.update(articleId, {
        not_helpful_count: article.not_helpful_count + 1
      });
    }
  }

  /**
   * Publish article
   */
  async publish(articleId: string, publishedBy: string): Promise<KnowledgeArticle> {
    return this.update(articleId, {
      status: 'published',
      published_at: new Date().toISOString(),
      updated_by: publishedBy
    });
  }

  /**
   * Archive article
   */
  async archive(articleId: string, archivedBy: string): Promise<KnowledgeArticle> {
    return this.update(articleId, {
      status: 'archived',
      archived_at: new Date().toISOString(),
      updated_by: archivedBy
    });
  }

  /**
   * Get knowledge base statistics
   */
  async getStatistics(): Promise<KnowledgeStatistics> {
    const { data: articles, error } = await this.client
      .from(this.config.tableName)
      .select('*');

    if (error) throw error;

    const stats: KnowledgeStatistics = {
      total_articles: articles?.length || 0,
      published_articles: 0,
      draft_articles: 0,
      archived_articles: 0,
      total_views: 0,
      total_helpful: 0,
      total_not_helpful: 0,
      average_helpfulness: 0,
      by_category: {},
      by_type: {},
      by_audience: {},
      most_viewed: [],
      most_helpful: [],
      recently_updated: []
    };

    if (!articles) return stats;

    // Calculate statistics
    articles.forEach(article => {
      // Status counts
      if (article.status === 'published') stats.published_articles++;
      if (article.status === 'draft') stats.draft_articles++;
      if (article.status === 'archived') stats.archived_articles++;

      // Usage metrics
      stats.total_views += article.view_count || 0;
      stats.total_helpful += article.helpful_count || 0;
      stats.total_not_helpful += article.not_helpful_count || 0;

      // Category breakdown
      stats.by_category[article.category] = (stats.by_category[article.category] || 0) + 1;

      // Type breakdown
      stats.by_type[article.article_type] = (stats.by_type[article.article_type] || 0) + 1;

      // Audience breakdown
      stats.by_audience[article.target_audience] = (stats.by_audience[article.target_audience] || 0) + 1;
    });

    // Calculate average helpfulness
    const totalFeedback = stats.total_helpful + stats.total_not_helpful;
    stats.average_helpfulness = totalFeedback > 0 
      ? stats.total_helpful / totalFeedback 
      : 0;

    // Most viewed articles (top 10)
    stats.most_viewed = articles
      .sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
      .slice(0, 10)
      .map(article => ({
        id: article.id,
        title: article.title,
        view_count: article.view_count || 0
      }));

    // Most helpful articles (top 10)
    stats.most_helpful = articles
      .sort((a, b) => (b.helpful_count || 0) - (a.helpful_count || 0))
      .slice(0, 10)
      .map(article => ({
        id: article.id,
        title: article.title,
        helpful_count: article.helpful_count || 0
      }));

    // Recently updated articles (top 10)
    stats.recently_updated = articles
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 10)
      .map(article => ({
        id: article.id,
        title: article.title,
        updated_at: article.updated_at
      }));

    return stats;
  }

  /**
   * Get category breakdown
   */
  async getCategoryBreakdown(): Promise<Record<string, number>> {
    const { data, error } = await this.client
      .from(this.config.tableName)
      .select('category');

    if (error) throw error;

    const breakdown: Record<string, number> = {};
    data?.forEach(article => {
      breakdown[article.category] = (breakdown[article.category] || 0) + 1;
    });

    return breakdown;
  }

  /**
   * Get articles needing review
   */
  async findNeedingReview(
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    return this.findByFilters({ status: 'review' }, options);
  }

  /**
   * Get draft articles by author
   */
  async findDraftsByAuthor(
    authorId: string,
    options?: QueryOptions
  ): Promise<KnowledgeArticle[]> {
    return this.findByFilters({ 
      created_by: authorId, 
      status: 'draft' 
    }, options);
  }
}
